import type { 
  AccountInfo,
  AccountLicense,
  UpdateAccountInfo, 
  ServiceContractInfo, 
  LicenseErrors, 
  ResetPasswordAccountInfo, 
  TemporaryAccountInfo, 
  InviteShareInfo,
  TemporaryAccountRequestInfo,
  TemporaryAccountRequestStatusInfo,
} from "@/types/accountApiDef"
import { defineStore } from "pinia"
import { ref, computed } from 'vue'
import { useAccountPreferencesStore } from "@/stores/accountPreferences"
import { useApiStore, apiIdOf, API_ORIGIN, axiosRetryx } from "@/stores/api"
import { OK, apiDefaultDescriptions, type ApiOptions } from "@/stores/api"
import { isPremium } from '@/utils/kuraemon_plans'
import { useLoginStore } from "@/stores/login"
import { useLoadingStore } from "@/stores/loading"

export const useAccountStore = defineStore('account', () => {
  const { getTalkNotificationMailSetting } = useAccountPreferencesStore()
  const { apiCall, checkResponseError } = useApiStore()
  const loginStore = useLoginStore()

  // アカウント情報
  const accountDetailInfo = ref<AccountInfo | null>(null) 

  // アカウント情報取得API
  const getAccountInfo = async (
    args: { 
      account_id?: string
      request_current_disk_byte?: boolean
      __apiOptions?: any
    }
  ): Promise<undefined | AccountInfo> => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('getAccountInfo');

    return await apiCall<AccountInfo>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/getaccountinfo";
      const data = {
        account_id: args.account_id,
        request_current_disk_byte: args.request_current_disk_byte,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }
      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }
      const apiResult = response.data.account_info;
      accountDetailInfo.value = apiResult;
      return apiResult;
    })
  }

  // アカウント情報更新API 
  const updateAccountInfo = async (
    args: { 
      update_items: {
        icon_id?: string
        icon_path?: string 
        account_name?: string 
        company_name?: string
        department_name?: string
      }
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('updateAccountInfo');

    return await apiCall<UpdateAccountInfo>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/updateaccountinfo";
      const data = {
        update_items: args.update_items,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }
      const updateAccountInfo = response.data.update_cloud_account_info;
      // 編集中のアカウント情報をマージ
        const mergedAccountDetailInfo = {
          ...accountDetailInfo.value,
          ...updateAccountInfo,
        }
        accountDetailInfo.value = mergedAccountDetailInfo

      // 現在のログインアカウントの情報を更新
        const currentAccountInfo: any = loginStore.currentAccount
        for(const k of Object.keys(currentAccountInfo)) {
          if(!Object.prototype.hasOwnProperty.call(updateAccountInfo, k)) continue;
          currentAccountInfo[k] = updateAccountInfo[k]
        }
        loginStore.updateCurrentAccount(currentAccountInfo)

        return updateAccountInfo;
    })
  }

  const accountLicenseInfo = ref<AccountLicense | null >(null)
  const displayPlan = computed(() => {
    if(!accountLicenseInfo.value) return null;
    if(accountLicenseInfo.value.next_account_service_contract_info){
      if(isPremium(accountLicenseInfo.value.next_account_service_contract_info.service_contract_type)){
        // 次のプラン情報がpremiumの場合のみ、それを優先して表示する
        return accountLicenseInfo.value.next_account_service_contract_info
      }
    }
    // それ以外は現在のプランを表示
    return accountLicenseInfo.value.account_service_contract_info;
  })

  // アカウントライセンス情報取得API
  const getAccountLicense = async (
    args: { 
      account_id?: string
      request_current_disk_byte?: boolean
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('getAccountLicense');

    return await apiCall<AccountLicense>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/getaccountlicense";
      const data = {
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }
      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }
      const apiResult = {
        account_service_contract_info: response.data.account_service_contract_info,
        next_account_service_contract_info: response.data.next_account_service_contract_info,
      }
      accountLicenseInfo.value = apiResult;
      return apiResult;
    })
  }

  const retrieveAccountPlanInfo = async() =>{
    const { loadingProc }  = useLoadingStore()
    const __apiOptions = { noDisplayLoading: true }
    const getAccountInfoProc = async () => {
      // アカウント情報取得
      return await getAccountInfo({ request_current_disk_byte: true, __apiOptions: structuredClone(__apiOptions) });
    }
    const getAccountLicenseProc = async () => {
      // アカウントライセンス情報取得
      return await getAccountLicense({ __apiOptions: structuredClone(__apiOptions) });
    }
    const getTalkNotificationMailSettingProc = async () => {
      // トーク通知メール設定取得API
      return await getTalkNotificationMailSetting({ __apiOptions: structuredClone(__apiOptions) });
    }

    return await loadingProc(async () => {
      const [accountDetailInfo, accountLicenseInfo, talkNotificationMailAnnotationInfo] = await Promise.all([
        getAccountInfoProc(),
        getAccountLicenseProc(),
        getTalkNotificationMailSettingProc(),
      ])
      if (!accountDetailInfo || !accountLicenseInfo || !talkNotificationMailAnnotationInfo) {
        return;
      }
      return { accountDetailInfo, accountLicenseInfo, talkNotificationMailAnnotationInfo }
    })
  }

  // ユーザーアイコンアップロードURL取得API
  const getAccountIconUploadUrl = async (
    args: { 
      file_info: {name: string, type: string}
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('getAccountIconUploadUrl');

    return await apiCall<{icon_id: string, url: string}>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/getaccounticonuploadurl";
      const data = {
        file_info: args.file_info,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }
      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }
      const apiResult = {
        icon_id: response.data.icon_id,
        url: response.data.url,
      }
      return apiResult;
    })
  }
  
  // アカウントライセンス登録API
  const registerAccountLicense = async (
    args: { 
      license_code: string
      license_register_route: string
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('registerAccountLicense');

    return await apiCall<{ 
      account_service_contract_info: ServiceContractInfo
    } | {
      license_error: LicenseErrors
    }>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/registeraccountlicense";
      const data = {
        license_code: args.license_code,
        license_register_route: args.license_register_route,
      }
      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      const licenseErrors: {[code: number]: LicenseErrors}= {
        401: 'INVALID_LICENSE',
        402: 'EXPIRED_LICENSE',
        410: 'EXCEEDED_PERIOD',
        403: 'USED_LICENSE',
        404: 'EXCEEDED_COUNT',
        405: 'INVALID_ACCOUNT_ADMIN_RULE_ENTERPRIZE',
        406: 'INVALID_ACCOUNT_BUSINESS_RULE_ENTERPRIZE',
        407: 'USED_LICENSE_ENTERPRIZE',
        400038: 'PACK_LICENSE_APPLIED_TO_SAME_LICENSED_ACCOUNT',
        400063: 'EXTENSION_PACK_NOT_ELIGIBLE_ACCOUNT',
      };
      type Code = keyof typeof licenseErrors

      const codes = [OK, ...Object.keys(licenseErrors)].map(cd => Number(cd))
      if (checkResponseError(response, descriptions, apiOptions, codes)) {
        return;
      }
      const licenseError = licenseErrors[response.data.code as Code]
      if(licenseError){
        return { license_error: licenseError };
      }

      const apiResult = {
        account_service_contract_info: response.data.account_service_contract_info,
      }
      return apiResult;
    })
  }

  // パスワード変更API
  const changePassword = async (
    args: { 
      old_password: string
      new_password: string
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('changePassword');

    return await apiCall<'OK'>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/changepassword";
      const data = {
        old_password: args.old_password,
        new_password: args.new_password,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }
      return 'OK';
    })
  }

  // パスワード再設定メール送信API
  const sendAccountPasswordResetMail = async (
    args: { 
      mail: string
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('sendAccountPasswordResetMail');

    return await apiCall<'OK'>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/sendaccountpasswordresetmail";
      const data = {
        mail: args.mail,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
        401: `メールアドレスが間違っている
または登録がされていません`,
      }

      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }
      return 'OK';
    })
  }

  // パスワード再設定情報取得API
  const getResetPasswordAccount = async (
    args: { 
      onetime_token: string
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('getResetPasswordAccount');

    return await apiCall<"EXPIRED_TOKEN" | ResetPasswordAccountInfo>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/getresetpasswordaccount";
      const data = {
        onetime_token: args.onetime_token,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      const EXPIRED_TOKEN_CODE = 401;
      if (checkResponseError(response, descriptions, apiOptions, [OK, EXPIRED_TOKEN_CODE])) {
        return; // エラー
      }
      if(response.data.code === EXPIRED_TOKEN_CODE){
        return "EXPIRED_TOKEN";
      }
      return response.data.account_info;
    })
  }

  // パスワード再設定API
  const resetAccountPassword = async (
    args: { 
      onetime_token: string
      password: string
      __apiOptions?: ApiOptions
    }
  ): Promise<undefined | "EXPIRED_TOKEN" | string > => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('resetAccountPassword');

    return await apiCall<"EXPIRED_TOKEN" | "OK">(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/resetaccountpassword";
      const data = {
        onetime_token: args.onetime_token,
        password: args.password,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
        403: `パスワードは8文字以上で
英大文字、英小文字、数字を含む必要があります`,
      }
      const EXPIRED_TOKEN_CODE = 401;
      if (checkResponseError(response, descriptions, apiOptions, [OK, EXPIRED_TOKEN_CODE])) {
        return; // エラー
      }
      if(response.data.code === EXPIRED_TOKEN_CODE){
        return "EXPIRED_TOKEN";
      }
      return "OK"
    })
  }

  // メールアドレス変更API
  const changeMailAddress = async (
    args: { 
      onetime_token: string
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('changeMailAddress');

    return await apiCall<"EXPIRED_TOKEN" | 'OK'>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/changemailaddress";
      const data = {
        onetime_token: args.onetime_token,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      const EXPIRED_TOKEN_CODE = 401;
      if (checkResponseError(response, descriptions, apiOptions, [OK, EXPIRED_TOKEN_CODE])) {
        return; // エラー
      }
      if(response.data.code === EXPIRED_TOKEN_CODE){
        return "EXPIRED_TOKEN";
      }
      return 'OK'
    })
  }


  // アカウント仮登録情報
  const temporaryAccountInfo = ref<TemporaryAccountInfo | null>(null)

  // アカウント仮登録情報取得API
  const getTemporaryAccount = async (
    args: { 
      onetime_token: string
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('getTemporaryAccount');

    return await apiCall<"EXPIRED_TEMPORARY_ACCOUNT" | "REAPPLYABLE_TEMPORARY_ACCOUNT" | TemporaryAccountInfo>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/gettemporaryaccount";
      const data = {
        onetime_token: args.onetime_token,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      // 無効な仮登録情報の場合のコード
      const EXPIRED_TEMPORARY_ACCOUNT_CODE = 401;
      const REAPPLYABLE_TEMPORARY_ACCOUNT_CODE = 402;
      const codes = [OK, EXPIRED_TEMPORARY_ACCOUNT_CODE, REAPPLYABLE_TEMPORARY_ACCOUNT_CODE];
      if (checkResponseError(response, descriptions, apiOptions, codes )) {
        return;
      }
      if(EXPIRED_TEMPORARY_ACCOUNT_CODE === response.data.code){
        return 'EXPIRED_TEMPORARY_ACCOUNT';
      }
      if(REAPPLYABLE_TEMPORARY_ACCOUNT_CODE === response.data.code){
        return 'REAPPLYABLE_TEMPORARY_ACCOUNT';
      }

      const apiResult = {
        mail: response.data.mail,
        app_user_name: response.data.app_user_name,
        contact_tel: response.data.contact_tel,
        register_route: response.data.register_route,
        company_name: response.data.company_name,
        department_name: response.data.department_name,
      }
      
      temporaryAccountInfo.value = apiResult;
      return apiResult;
    })
  }

  // 招待/データ共有内容取得API
  const getInviteShareInfo = async (
    args: { 
      onetime_token: string
      __apiOptions?: ApiOptions
    }
  ): Promise<undefined | InviteShareInfo> => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('getInviteShareInfo');

    return await apiCall(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/getinviteshareinfo";
      const data = {
        onetime_token: args.onetime_token,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      if (checkResponseError(response, descriptions, apiOptions)) {
        return;
      }
      return response.data.invite_share_info;
    })
  }

  // トークンからアカウント登録情報取得API
  const getTemporaryAccountRequestInfo = async (
    args: { 
      onetime_token: string
      __apiOptions?: ApiOptions
    }
  ): Promise<undefined | 'TOKEN_EXPIRED' | TemporaryAccountRequestInfo > => {

    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('getTemporaryAccountRequestInfo');

    return await apiCall<'TOKEN_EXPIRED' | TemporaryAccountRequestInfo>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/gettemporaryaccountrequestinfo";
      const data = {
        onetime_token: args.onetime_token,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }
      const TOKEN_EXPIRED = 401009
      if (checkResponseError(response, descriptions, apiOptions, [OK, TOKEN_EXPIRED])) {
        return;
      }
      if (response.data.code === TOKEN_EXPIRED) return 'TOKEN_EXPIRED'
      const apiResult = {
        temporary_account_request_info: response.data.temporary_account_request_info,
        condition: response.data.condition,
      }
      return apiResult;
    })
  }

    // アカウント仮登録要求状況確認API
    const getTemporaryAccountRequestStatus = async (
      args: { 
        account_slot_code: string
        __apiOptions?: ApiOptions
      }
    ) => {
      const apiOptions = args.__apiOptions || {}
      apiOptions.apiId = apiIdOf('getTemporaryAccountRequestStatus');

      return await apiCall<TemporaryAccountRequestStatusInfo>(apiOptions, async (apiConfig) => {
        const url = API_ORIGIN + "/gettemporaryaccountrequeststatus";
        const data = {
          account_slot_code: args.account_slot_code,
        }

        const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

        const descriptions = {
          ...apiDefaultDescriptions,
        }

        if (checkResponseError(response, descriptions, apiOptions)) {
          return;
        }
        return response.data.status_info;
      })
    }

  // 期限切れアカウント本登録再申請API
  const reapplyExpiredAccountRequest = async (
    args: { 
      onetime_token: string
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('reapplyExpiredAccountRequest');

    return await apiCall<"ALREADY_APPLIED" | "REGISTRATION_COMPLETED" | "OK">(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/reapplyexpiredaccountrequest";
      const data = {
        onetime_token: args.onetime_token,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      const ALREADY_APPLIED = 400001;
      const REGISTRATION_COMPLETED = 400002;
      const codes = [OK, ALREADY_APPLIED, REGISTRATION_COMPLETED]
      if (checkResponseError(response, descriptions, apiOptions, codes)) {
        return;
      }
      if(response.data.code === ALREADY_APPLIED){
        return 'ALREADY_APPLIED';
      }
      if(response.data.code === REGISTRATION_COMPLETED){
        return 'REGISTRATION_COMPLETED';
      }
      return 'OK';
    })
  }
  
  // トークンからアカウント仮登録要求API
  const registerTemporaryAccountRequestWithToken = async (
    args: { 
      onetime_token: string
      mail: string
      user_name: string
      company_name: string
      company_tel: string
      company_postno: string
      company_prefecture: string
      company_address: string
      contact_tel: string
      request_random_value: number
      is_sole_proprietor: boolean
      __apiOptions?: ApiOptions
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('registerTemporaryAccountRequestWithToken');

    return await apiCall<string>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/registertemporaryaccountrequestwithtoken";
      const data = {
        onetime_token: args.onetime_token,
        mail: args.mail,
        user_name: args.user_name,
        company_name: args.company_name,
        company_tel: args.company_tel,
        company_postno: args.company_postno,
        company_prefecture: args.company_prefecture,
        company_address: args.company_address,
        contact_tel: args.contact_tel,
        request_random_value: args.request_random_value,
        is_sole_proprietor: args.is_sole_proprietor,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      const errorDefs = {
        400041: 'DUPLICATE_MAILADDR', // 重複メールアドレスのエラーコード
        401009: 'TOKEN_EXPIRED',      // ワンタイムトークン期限切れエラーコード
      }
      type Code = keyof typeof errorDefs
      const codes = [OK, ...Object.keys(errorDefs)].map(cd => Number(cd))
      if (checkResponseError(response, descriptions, apiOptions, codes)) {
        return;
      }
      const theError = errorDefs[response.data.code as Code]
      if (theError) return theError
      return 'OK';
    })
  }

  // アカウント仮登録要求API
  const registerTemporaryAccountRequest = async (
    args: { 
      account_slot_code: string
      mail: string
      user_name: string
      company_name: string
      company_tel: string
      company_postno: string
      company_prefecture: string
      company_address: string
      file_md5?: string,
      contact_tel: string
      request_random_value: number
      regist_device?: string 
      regist_route?: string | null
      invite_share?: boolean
      request_code?: string | null,
      is_sole_proprietor: boolean
      __apiOptions?: any
    }
  ) => {
    const apiOptions = args.__apiOptions || {}
    apiOptions.apiId = apiIdOf('registerTemporaryAccountRequest');

    return await apiCall<'OK' | 'DUPLICATE_MAILADDR'>(apiOptions, async (apiConfig) => {
      const url = API_ORIGIN + "/registertemporaryaccountrequest";
      const data = {
        account_slot_code: args.account_slot_code,
        mail: args.mail,
        user_name: args.user_name,
        company_name: args.company_name,
        company_tel: args.company_tel,
        company_postno: args.company_postno,
        company_prefecture: args.company_prefecture,
        company_address: args.company_address,
        file_md5: args.file_md5,
        request_random_value: args.request_random_value,
        contact_tel: args.contact_tel,
        regist_device: args.regist_device,
        regist_route: args.regist_route,
        invite_share: args.invite_share,
        request_code: args.request_code,
        is_sole_proprietor: args.is_sole_proprietor,
      }

      const response = await axiosRetryx({ method: 'post', url, data, ...apiConfig })

      const descriptions = {
        ...apiDefaultDescriptions,
      }

      const DUPLICATE_MAILADDR = 402;
      if (checkResponseError(response, descriptions, apiOptions, [OK, DUPLICATE_MAILADDR])) {
        return;
      }
      if(response.data.code === DUPLICATE_MAILADDR){
        return 'DUPLICATE_MAILADDR';
      }

      return 'OK';
    })
  }

  return {
    accountDetailInfo,
    displayPlan,

    getAccountInfo,
    updateAccountInfo,
    getAccountLicense,
    retrieveAccountPlanInfo,
    getAccountIconUploadUrl,
    registerAccountLicense,
    changePassword,
    sendAccountPasswordResetMail,
    getResetPasswordAccount,
    resetAccountPassword,
    changeMailAddress,

    temporaryAccountInfo,
    getTemporaryAccount,
    getInviteShareInfo,
    getTemporaryAccountRequestInfo,
    getTemporaryAccountRequestStatus,
    reapplyExpiredAccountRequest,
    registerTemporaryAccountRequestWithToken, 
    registerTemporaryAccountRequest,
  }
})